"use client"

import { useState, useContext, useEffect, useRef } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import {
  Button,
  Typography,
  Container,
  Modal,
  Box,
  CircularProgress,
  IconButton,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import SaveIcon from "@mui/icons-material/Save"
import PersonIcon from "@mui/icons-material/Person"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import FormularioEntidadComponent from "./FormularioEntidadComponent.js"
import FileUploadDragDrop from "../transaccionComponent/FileUploadDragDrop.js"

const TIPO_ENTIDAD_POR_CONTEXTO = {
  cliente: 1,
  proveedor: 2,
}

/** Id por defecto según prop tipoEntidad, validado contra la lista del backend (sin asumir ids fijos). */
function resolveDefaultTipoEntidadId(tipoEntidades, tipoEntidad) {
  if (!tipoEntidades?.length) return null
  const mapped = TIPO_ENTIDAD_POR_CONTEXTO[tipoEntidad]
  const numericIds = new Set(tipoEntidades.map((t) => Number(t.id)))
  if (mapped != null && numericIds.has(Number(mapped))) {
    return Number(mapped)
  }
  const needle = tipoEntidad === "proveedor" ? "proveedor" : "cliente"
  const found = tipoEntidades.find((t) =>
    String(t.descripcion || "").toLowerCase().includes(needle),
  )
  if (found != null) return Number(found.id)
  return Number(tipoEntidades[0].id)
}

const CrearEntidadClienteModalComponent = ({ open, handleClose, entidad, isEditing, handleAlerta, onClienteCreado, tipoEntidad = "cliente", ocultarInformacionContacto = false }) => {
  const { tipoEntidades, getTipoEntidad, postEntidad, updateEntidad, condicionIva, getCondicionIva, getCondicionIvaByEntidad, existeProveedorConDNI, isNavbarCollapsed } = useContext(ErpContext)

  // Estados para los campos del formulario
  const [descripcionEntidad, setDescripcionEntidad] = useState("")
  const [apellidoEntidad, setApellidoEntidad] = useState("")
  const [telefonoEntidad, setTelefonoEntidad] = useState("")
  const [emailEntidad, setEmailEntidad] = useState("")
  const [dniCuitCuilEntidad, setdniCuitCuilEntidad] = useState("")
  const [direccionEntidad, setDireccionEntidad] = useState("")
  const [localidadEntidad, setLocalidadEntidad] = useState("")
  const [provinciaEntidad, setProvinciaEntidad] = useState("")
  const [idTipoEntidadEntidad, setIdTipoEntidadEntidad] = useState("")
  const [idCondicionIvaEntidad, setIdCondicionIvaEntidad] = useState("")
  
  // NUEVOS ESTADOS
  const [sexoEntidad, setSexoEntidad] = useState("")
  const [fechaNacimientoEntidad, setFechaNacimientoEntidad] = useState(null)
  const [paisEntidad, setPaisEntidad] = useState("")
  const [codigoPostalEntidad, setCodigoPostalEntidad] = useState("")
  const [observacionesEntidad, setObservacionesEntidad] = useState("")
  const [esTutorEntidad, setEsTutorEntidad] = useState(false)
  const [nombreTutorEntidad, setNombreTutorEntidad] = useState("")
  const [apellidoTutorEntidad, setApellidoTutorEntidad] = useState("")
  const [telefonoTutorEntidad, setTelefonoTutorEntidad] = useState("")
  // DATOS BANCARIOS
  const [nombreBancoEntidad, setNombreBancoEntidad] = useState("")
  const [numeroCuentaEntidad, setNumeroCuentaEntidad] = useState("")
  const [cbuCvuCuentaEntidad, setCbuCvuCuentaEntidad] = useState("")
  const [aliasCuentaEntidad, setAliasCuentaEntidad] = useState("")
  // ES VENDEDOR
  const [esVendedorEntidad, setEsVendedorEntidad] = useState(false)
  // ARCHIVOS ADJUNTOS
  const [adjuntosEntidad, setAdjuntosEntidad] = useState([])
  const [adjuntosExistentes, setAdjuntosExistentes] = useState([])

  // Estados adicionales para UI
  const [isLoading, setIsLoading] = useState(false)
  const [catalogosListos, setCatalogosListos] = useState(false)
  const [errors, setErrors] = useState({})
  const isFirstRender = useRef(true)

  // Etiquetas dinámicas según el tipo de entidad
  const etiquetas = {
    cliente: {
      crear: "Crear Nuevo Cliente",
      actualizar: "Actualizar Cliente"
    },
    proveedor: {
      crear: "Crear Nuevo Proveedor",
      actualizar: "Actualizar Proveedor"
    }
  }

  const labels = etiquetas[tipoEntidad]

  // Mapeo de tipo de entidad según contexto
  const mostrarAdjuntos = [1, 2].includes(Number(idTipoEntidadEntidad || TIPO_ENTIDAD_POR_CONTEXTO[tipoEntidad]))

  // Catálogos: solo al abrir/cerrar (no al volver del fetch) — esperar API antes de mostrar el formulario
  useEffect(() => {
    if (!open) {
      setCatalogosListos(false)
      return
    }
    let cancelled = false
    ;(async () => {
      setCatalogosListos(false)
      try {
        const tasks = []
        if (tipoEntidades.length === 0) tasks.push(getTipoEntidad())
        if (condicionIva.length === 0) tasks.push(getCondicionIva())
        await Promise.all(tasks)
      } catch (e) {
        console.error("Error cargando catálogos del modal de entidad:", e)
      } finally {
        if (!cancelled) setCatalogosListos(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, getTipoEntidad, getCondicionIva])

  // Tipo por defecto en creación: solo cuando ya hay opciones cargadas (coherente con tipoEntidad)
  useEffect(() => {
    if (!open || !catalogosListos || isEditing || entidad) return
    if (!tipoEntidades?.length) return
    const id = resolveDefaultTipoEntidadId(tipoEntidades, tipoEntidad)
    if (id != null && !Number.isNaN(id)) setIdTipoEntidadEntidad(id)
  }, [open, catalogosListos, isEditing, entidad, tipoEntidades, tipoEntidad])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return // Evita la ejecución en el primer render
    }

    const cargarDatosEntidad = async () => {
      if (entidad) {
        // Cuando la entidad cambia, actualizar los estados de los campos
        setDescripcionEntidad(entidad.descripcion || "")
        setApellidoEntidad(entidad.apellido || "")
        setTelefonoEntidad(entidad.telefono || "")
        setEmailEntidad(entidad.email || "")
        setdniCuitCuilEntidad(entidad.dniCuitCuil || "")
        setDireccionEntidad(entidad.direccion || "")
        setLocalidadEntidad(entidad.localidad || "")
        setProvinciaEntidad(entidad.provincia || "")
        setIdTipoEntidadEntidad(entidad.idTipoEntidad || "")
        
        // NUEVOS CAMPOS
        setSexoEntidad(entidad.sexo || "")
        setFechaNacimientoEntidad(entidad.fechaNacimiento || null)
        setPaisEntidad(entidad.pais || "")
        setCodigoPostalEntidad(entidad.codigoPostal || "")
        setObservacionesEntidad(entidad.observaciones || "")
        setEsTutorEntidad(entidad.esTutor || false)
        setNombreTutorEntidad(entidad.nombreTutor || "")
        setApellidoTutorEntidad(entidad.apellidoTutor || "")
        setTelefonoTutorEntidad(entidad.telefonoTutor || "")
        // DATOS BANCARIOS
        setNombreBancoEntidad(entidad.nombreBanco || "")
        setNumeroCuentaEntidad(entidad.numeroCuenta || "")
        setCbuCvuCuentaEntidad(entidad.cbuCvuCuenta || "")
        setAliasCuentaEntidad(entidad.aliasCuenta || "")
        // ES VENDEDOR
        setEsVendedorEntidad(entidad.esVendedor || false)
        // ARCHIVOS ADJUNTOS
        if (entidad.adjuntos) {
          try {
            const parsed = JSON.parse(entidad.adjuntos)
            setAdjuntosExistentes(Array.isArray(parsed) ? parsed : [])
          } catch (e) {
            setAdjuntosExistentes([])
          }
        } else {
          setAdjuntosExistentes([])
        }
        setAdjuntosEntidad([])

        // Obtener la condición IVA desde el backend
        if (entidad.id && isEditing) {
          try {
            const condicionIvaData = await getCondicionIvaByEntidad(entidad.id)
            setIdCondicionIvaEntidad(condicionIvaData?.idCondicionIva || "")
          } catch (error) {
            console.error("Error obteniendo condición IVA:", error)
            setIdCondicionIvaEntidad("")
          }
        } else {
          setIdCondicionIvaEntidad(entidad.idCondicionIva || "")
        }
      } else {
        resetForm()
      }
    }

    cargarDatosEntidad()
  }, [entidad, isEditing, getCondicionIvaByEntidad]) // Este efecto se ejecutará cada vez que `entidad` cambie

  // Función para validar el formulario
  const validateForm = () => {
    const newErrors = {}

    if (!descripcionEntidad.trim()) {
      newErrors.descripcion = "El nombre es requerido"
    }

    if (!dniCuitCuilEntidad.trim()) {
      newErrors.dniCuitCuil = "El DNI/CUIT/CUIL es requerido"
    }

    if (emailEntidad && !/\S+@\S+\.\S+/.test(emailEntidad)) {
      newErrors.email = "Email inválido"
    }

    if (!idTipoEntidadEntidad) {
      newErrors.idTipoEntidad = "Debe seleccionar un tipo de entidad"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const limpiarCamposComunes = () => {
    setDescripcionEntidad("")
    setApellidoEntidad("")
    setTelefonoEntidad("")
    setEmailEntidad("")
    setdniCuitCuilEntidad("")
    setDireccionEntidad("")
    setLocalidadEntidad("")
    setProvinciaEntidad("")
    setIdCondicionIvaEntidad("")
    // NUEVOS CAMPOS
    setSexoEntidad("")
    setFechaNacimientoEntidad(null)
    setPaisEntidad("")
    setCodigoPostalEntidad("")
    setObservacionesEntidad("")
    setEsTutorEntidad(false)
    setNombreTutorEntidad("")
    setApellidoTutorEntidad("")
    setTelefonoTutorEntidad("")
    // DATOS BANCARIOS
    setNombreBancoEntidad("")
    setNumeroCuentaEntidad("")
    setCbuCvuCuentaEntidad("")
    setAliasCuentaEntidad("")
    // ES VENDEDOR
    setEsVendedorEntidad(false)
    // ARCHIVOS ADJUNTOS
    setAdjuntosEntidad([])
    setAdjuntosExistentes([])
    setErrors({})
  }

  /** Reset completo (tras guardar o cierre). En creación, el id de tipo lo vuelve a fijar el efecto cuando hay catálogo. */
  const resetForm = () => {
    limpiarCamposComunes()
    setIdTipoEntidadEntidad("")
  }

  const handleEntidadSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation() // Evita que el evento se propague al formulario padre

    // Validar formulario
    if (!validateForm()) {
      return
    }

    // Validar DNI único para proveedores (al enviar)
    if (tipoEntidad === "proveedor" && dniCuitCuilEntidad?.trim()) {
      const existe = await existeProveedorConDNI(dniCuitCuilEntidad.trim(), isEditing ? entidad?.id : null)
      if (existe) {
        setErrors((prev) => ({ ...prev, dniCuitCuil: "Ya existe un proveedor con ese DNI/CUIT/CUIL" }))
        return
      }
    }

    try {
      setIsLoading(true)

      if (isEditing) {
        // Si es una edición, actualiza la entidad existente
        await updateEntidad(
          entidad.id,
          descripcionEntidad,
          apellidoEntidad,
          telefonoEntidad,
          emailEntidad,
          dniCuitCuilEntidad,
          direccionEntidad,
          localidadEntidad,
          provinciaEntidad,
          idTipoEntidadEntidad,
          idCondicionIvaEntidad,
          // NUEVOS PARÁMETROS
          sexoEntidad,
          fechaNacimientoEntidad,
          paisEntidad,
          codigoPostalEntidad,
          observacionesEntidad,
          esTutorEntidad,
          nombreTutorEntidad,
          apellidoTutorEntidad,
          telefonoTutorEntidad,
          // DATOS BANCARIOS
          nombreBancoEntidad,
          numeroCuentaEntidad,
          cbuCvuCuentaEntidad,
          aliasCuentaEntidad,
          esVendedorEntidad,
          [...adjuntosExistentes, ...adjuntosEntidad],
        )
      } else {
        // Si no, crea una nueva entidad
        const clienteCreado = await postEntidad(
          descripcionEntidad,
          apellidoEntidad,
          telefonoEntidad,
          emailEntidad,
          dniCuitCuilEntidad,
          direccionEntidad,
          localidadEntidad,
          provinciaEntidad,
          idTipoEntidadEntidad,
          idCondicionIvaEntidad,
          // NUEVOS PARÁMETROS
          sexoEntidad,
          fechaNacimientoEntidad,
          paisEntidad,
          codigoPostalEntidad,
          observacionesEntidad,
          esTutorEntidad,
          nombreTutorEntidad,
          apellidoTutorEntidad,
          telefonoTutorEntidad,
          // DATOS BANCARIOS
          nombreBancoEntidad,
          numeroCuentaEntidad,
          cbuCvuCuentaEntidad,
          aliasCuentaEntidad,
          esVendedorEntidad,
          null,             // extraOptions
          adjuntosEntidad,
        )

        // Si se creó un cliente nuevo y existe la función callback, llamarla
        if (clienteCreado && onClienteCreado) {
          const clienteData = {
            id: clienteCreado.id,
            label: `${descripcionEntidad} ${apellidoEntidad} ${dniCuitCuilEntidad}`,
            descripcion: descripcionEntidad,
            apellido: apellidoEntidad,
            dniCuitCuil: dniCuitCuilEntidad,
            telefono: telefonoEntidad,
            email: emailEntidad,
            direccion: direccionEntidad,
            localidad: localidadEntidad,
            provincia: provinciaEntidad,
            idTipoEntidad: idTipoEntidadEntidad,
            idCondicionIva: idCondicionIvaEntidad,
          }
          onClienteCreado(clienteData)
        }
      }

      // Limpiar los campos después de la acción
      resetForm()
      handleAlerta(true)
      handleClose()
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "Error al guardar"
      if (error?.response?.status === 409 && tipoEntidad === "proveedor") {
        setErrors((prev) => ({ ...prev, dniCuitCuil: msg }))
      } else {
        handleAlerta(false)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <div
        className="modalContenido"
        style={{
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "0",
          width: "98%",
          maxWidth: "900px",
          maxHeight: "95vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: "#5b5b5f",
            color: "white",
            padding: "14px 20px",
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            width: "100%",
            boxSizing: "border-box",
            position: "relative",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <PersonIcon style={{ marginRight: "12px", color: "rgb(244, 177, 14)" }} />
            <Typography variant="h6" style={{ fontWeight: "500" }}>
              {isEditing ? labels.actualizar : labels.crear}
            </Typography>
          </div>
          <IconButton 
            onClick={handleClose} 
            size="small" 
            style={{ 
              color: "white",
              position: "absolute",
              right: "8px",
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <Container 
          className="contenedorModal containerModuloDiseñoGeneral" 
          style={{ 
            padding: "20px",
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {!catalogosListos ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 280,
                flex: 1,
              }}
            >
              <CircularProgress size={48} thickness={4} sx={{ color: "#5b5b5f" }} />
            </Box>
          ) : (
          <form onSubmit={handleEntidadSubmit} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
              <FormularioEntidadComponent
              descripcionEntidad={descripcionEntidad}
              setDescripcionEntidad={setDescripcionEntidad}
              apellidoEntidad={apellidoEntidad}
              setApellidoEntidad={setApellidoEntidad}
              dniCuitCuilEntidad={dniCuitCuilEntidad}
              setdniCuitCuilEntidad={setdniCuitCuilEntidad}
              tipoEntidad={tipoEntidad}
              idEntidadActual={entidad?.id}
              setErrors={setErrors}
              existeProveedorConDNI={existeProveedorConDNI}
              telefonoEntidad={telefonoEntidad}
              setTelefonoEntidad={setTelefonoEntidad}
              emailEntidad={emailEntidad}
              setEmailEntidad={setEmailEntidad}
              direccionEntidad={direccionEntidad}
              setDireccionEntidad={setDireccionEntidad}
              localidadEntidad={localidadEntidad}
              setLocalidadEntidad={setLocalidadEntidad}
              provinciaEntidad={provinciaEntidad}
              setProvinciaEntidad={setProvinciaEntidad}
              idTipoEntidadEntidad={idTipoEntidadEntidad}
              setIdTipoEntidadEntidad={setIdTipoEntidadEntidad}
              idCondicionIvaEntidad={idCondicionIvaEntidad}
              setIdCondicionIvaEntidad={setIdCondicionIvaEntidad}
              tipoEntidades={tipoEntidades}
              condicionIva={condicionIva}
              errors={errors}
              showCondicionIva={true}
              compact={true}
              tipoEntidadReadonly={true}
              tipoEntidadPredeterminado={TIPO_ENTIDAD_POR_CONTEXTO[tipoEntidad]}
              sexoEntidad={sexoEntidad}
              setSexoEntidad={setSexoEntidad}
              fechaNacimientoEntidad={fechaNacimientoEntidad}
              setFechaNacimientoEntidad={setFechaNacimientoEntidad}
              paisEntidad={paisEntidad}
              setPaisEntidad={setPaisEntidad}
              codigoPostalEntidad={codigoPostalEntidad}
              setCodigoPostalEntidad={setCodigoPostalEntidad}
              observacionesEntidad={observacionesEntidad}
              setObservacionesEntidad={setObservacionesEntidad}
              esTutorEntidad={esTutorEntidad}
              setEsTutorEntidad={setEsTutorEntidad}
              nombreTutorEntidad={nombreTutorEntidad}
              setNombreTutorEntidad={setNombreTutorEntidad}
              apellidoTutorEntidad={apellidoTutorEntidad}
              setApellidoTutorEntidad={setApellidoTutorEntidad}
              telefonoTutorEntidad={telefonoTutorEntidad}
              setTelefonoTutorEntidad={setTelefonoTutorEntidad}
              ocultarInformacionContacto={ocultarInformacionContacto || tipoEntidad === "proveedor"}
              // DATOS BANCARIOS
              nombreBancoEntidad={nombreBancoEntidad}
              setNombreBancoEntidad={setNombreBancoEntidad}
              numeroCuentaEntidad={numeroCuentaEntidad}
              setNumeroCuentaEntidad={setNumeroCuentaEntidad}
              cbuCvuCuentaEntidad={cbuCvuCuentaEntidad}
              setCbuCvuCuentaEntidad={setCbuCvuCuentaEntidad}
              aliasCuentaEntidad={aliasCuentaEntidad}
              setAliasCuentaEntidad={setAliasCuentaEntidad}
              esVendedorEntidad={esVendedorEntidad}
              setEsVendedorEntidad={setEsVendedorEntidad}
              extraContent={
                mostrarAdjuntos ? (
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <CloudUploadIcon sx={{ mr: 1, color: "rgb(244, 177, 14)" }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#5b5b5f" }}>
                        Archivos Adjuntos
                      </Typography>
                    </Box>
                    <FileUploadDragDrop
                      files={adjuntosEntidad}
                      onFilesChange={setAdjuntosEntidad}
                      existingFiles={adjuntosExistentes}
                      onExistingFilesChange={setAdjuntosExistentes}
                      maxFiles={5}
                      maxTotalSizeMB={10}
                    />
                  </Box>
                ) : null
              }
            />

            {/* Sección: Archivos Adjuntos */}
            </Box>

            {/* Botones de acción */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 3,
                mb: 1,
                gap: 2,
                flexShrink: 0,
                pt: 2,
                borderTop: "1px solid rgba(0, 0, 0, 0.1)",
              }}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={handleClose}
                disabled={isLoading}
                style={{
                  padding: "10px 24px",
                  borderRadius: "8px",
                  fontWeight: "500",
                  borderWidth: "2px",
                  borderColor: "#5b5b5f",
                  color: "#5b5b5f",
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                type="submit"
                startIcon={isLoading ? null : <SaveIcon />}
                disabled={isLoading}
                style={{
                  padding: "10px 24px",
                  borderRadius: "8px",
                  fontWeight: "500",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  backgroundColor: "#5b5b5f",
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : isEditing ? "Actualizar" : "Guardar"}
              </Button>
            </Box>
          </form>
          )}
        </Container>

        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              borderRadius: "16px",
            }}
          >
            <CircularProgress size={60} thickness={4} />
          </div>
        )}
      </div>
    </Modal>
  )
}

export default CrearEntidadClienteModalComponent
